import { nanoid } from 'nanoid'
import { Form as FormAPI } from '@chilipiper/api-type-def'
import { Session } from '@chilipiper/models'
import { hasHubspot } from '@chilipiper/service/src/session'
import {
  CreatePersonalWorkspaceFormPayload,
  CreateWorkspaceFormPayload,
  form as formService,
} from '@chilipiper/service/src/form'
import { FieldType, OptionsType, Form as FormType, Field } from '../types'

const DEFAULT_LOCALE = 'en_US'

export class Form {
  static async getById(id: string): Promise<FormType> {
    const form = await formService.byId(id)

    return Form.mapFormResult(form)
  }

  static async getAll(workspaceId?: string): Promise<FormType[]> {
    const result = await (workspaceId
      ? formService.getAll(workspaceId)
      : formService.getAllByUser())

    return result.map(r => Form.mapFormResult(r))
  }

  static async delete(id: string) {
    return formService.delete(id)
  }

  static async saveField(
    form: FormType,
    { optionsType, ...field }: Field,
    language?: string
  ): Promise<FormType> {
    const nextForm = { ...form }

    const { lead, contact } = this.resetMappingField({ ...field, optionsType })

    nextForm.fields[field.index] = {
      ...field,
      options: optionsType === OptionsType.Custom ? field.options : [],
      pickListId: optionsType === OptionsType.CRM ? field.pickListId : undefined,
      leadSFField: lead,
      contactSFField: contact,
      label:
        language && language !== DEFAULT_LOCALE
          ? field.locales?.[DEFAULT_LOCALE]?.label
          : field.label,
      contactHSField: contact,
      locales: {
        ...field.locales,
        [language ? language : DEFAULT_LOCALE]: {
          label: field.label,
          error: `${field.label} is required`,
        },
      },
    }

    const formApi = mapFormApi(nextForm)

    await formService.save(formApi)

    return nextForm as FormType
  }

  static resetMappingField(field: Field) {
    switch (field.optionsType) {
      case OptionsType.CRM:
        return {
          lead: hasHubspot() ? '' : field.pickListId,
          contact: hasHubspot() ? field.pickListId : '',
        }
      case OptionsType.Custom: {
        if (field.pickListId) {
          return { lead: '', contact: '' }
        }

        return {
          lead: field.leadSFField,
          contact: hasHubspot() ? field.contactHSField : field.contactSFField,
        }
      }
      default: {
        if (field.pickListId) {
          return { lead: '', contact: '' }
        }

        return {
          lead: field.leadSFField,
          contact: hasHubspot() ? field.contactHSField : field.contactSFField,
        }
      }
    }
  }

  static async updateForm(form: FormType): Promise<FormType> {
    const formApi = mapFormApi(form)

    await formService.save(formApi)

    return form
  }

  static async createForm(workspaceId: string | undefined, formName: string) {
    const createFormPayload = {
      type: workspaceId ? 'Wid' : 'Uid',
      workspaceId: workspaceId,
      userId: !workspaceId ? Session.getCurrentUserId() : undefined,
    } as CreatePersonalWorkspaceFormPayload | CreateWorkspaceFormPayload
    const form = await formService.create(createFormPayload)
    await formService.save({
      ...form,
      ...createFormPayload,
      name: formName,
    })
    return {
      ...form,
      name: formName,
    }
  }

  static getOptionType(field: Field) {
    switch (field.fieldType) {
      case FieldType.Select:
      case FieldType.SingleSelection:
      case FieldType.MultipleSelection:
        if (field.options?.length > 0) {
          return OptionsType.Custom
        }

        if (field.pickListId) {
          return OptionsType.CRM
        }

        return OptionsType.None
      default:
        return OptionsType.None
    }
  }

  static mapFormResult(form: FormAPI, isHubspot?: boolean) {
    const { blocks, ...raw } = form
    return {
      ...raw,
      fields: blocks
        .filter(field => Boolean(field.fieldName))
        .map((b, index) => {
          const contactField = isHubspot ? (b.contactHSField ?? '') : (b.contactSFField ?? '')
          const leadField = isHubspot ? '' : (b.leadSFField ?? '')
          return {
            ...b,
            contactField,
            leadField,
            index,
            pickListId: b.pickListId,
            fieldType: mapViewModelFieldType(b.fieldType),
            label: b.label || '',
            id: nanoid(),
          }
        }),
    }
  }

  // eslint-disable-next-line complexity
  static mapHotFieldType(type = '', subType = '') {
    switch (type) {
      case 'textarea':
      case 'Textarea': {
        return FieldType.Multiline
      }
      case 'phone':
        return FieldType.Phone
      case 'email':
        return FieldType.Email
      case 'address':
        return FieldType.PhysicalAddress
      case 'multipicklist':
        return FieldType.MultipleSelection
      case 'picklist':
      case 'enumeration':
        if (subType === 'Checkbox') {
          return FieldType.MultipleSelection
        }
        return FieldType.Select
      case 'double':
      case 'percent':
      case 'int':
        return FieldType.Number
      default:
        return FieldType.SingleLine
    }
  }
}

function mapViewModelFieldType(type = '') {
  switch (type) {
    case 'Email':
      return FieldType.Email
    case 'Text':
      return FieldType.SingleLine
    case 'Multiline':
      return FieldType.Multiline
    case 'Select':
      return FieldType.Select
    case 'Phone':
      return FieldType.Phone
    case 'Number':
      return FieldType.Number
    case 'SingleSelection':
      return FieldType.SingleSelection
    case 'MultipleSelection':
      return FieldType.MultipleSelection
    default:
      return FieldType.SingleLine
  }
}

function mapFormApi({ fields, ...form }: FormType) {
  return {
    ...form,
    blocks: fields.map(({ id, fieldType, leadField, contactField, ...b }, index) => ({
      ...b,
      index: index,
      fieldType: mapApiFieldType(fieldType),
    })),
  }
}

function mapApiFieldType(type: FieldType) {
  switch (type) {
    case FieldType.Email:
      return 'Email'
    case FieldType.SingleLine:
      return 'Text'
    case FieldType.Multiline:
      return 'Multiline'
    case FieldType.Select:
      return 'Select'
    case FieldType.Phone:
      return 'Phone'
    case FieldType.Number:
      return 'Number'
    case FieldType.SingleSelection:
      return 'SingleSelection'
    case FieldType.MultipleSelection:
      return 'MultipleSelection'
    default:
      return 'Text'
  }
}

export class CrmMappingViewModel extends Form {
  static resetMappingField(field: Field) {
    return {
      lead: field.leadSFField,
      contact: hasHubspot() ? field.contactHSField : field.contactSFField,
    }
  }
}
